import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  Cable,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  FileSpreadsheet,
  Globe2,
  Link2,
  Loader2,
  PackageSearch,
  Receipt,
  ShieldCheck,
  Unplug,
} from "lucide-react";

import { functions } from "../../firebase";
import { logAuditEvent } from "../../utils/auditLogger";

const inputClasses =
  "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500";
const checkboxClasses = "h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500";
const connectorOrder = ["googleSheets", "quickbooks", "hubspot", "salesforce"];

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : value?.seconds
        ? new Date(value.seconds * 1000)
        : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString();
  } catch {
    return "—";
  }
};

const stepContent = {
  googleSheets: [
    "Choose the spreadsheet that will act as your import source for accounts, inventory, or both.",
    "Paste the Google Sheet URL and the tab name you want SupplyHawk to use first.",
    "Save the connection, then use the selected targets as your import workflow source of truth.",
  ],
  quickbooks: [
    "Open your QuickBooks app credentials and copy the Realm ID, Client ID, and Client Secret.",
    "Choose whether this is a sandbox or live company, then decide what you want to import first.",
    "Save the setup so SupplyHawk can use QuickBooks as your finance-side source for item or customer data.",
  ],
  hubspot: [
    "Create or open a HubSpot private app and copy its access token.",
    "Decide whether HubSpot should feed companies, contacts, or both into SupplyHawk.",
    "Save the connection so HubSpot can become your easier CRM source before a heavier enterprise rollout.",
  ],
  salesforce: [
    "Create or open a Salesforce Connected App in Setup.",
    "Enable OAuth and copy your Consumer Key and Consumer Secret.",
    "Use a Salesforce user with API access, then enter the credentials below to connect.",
  ],
};

const connectorMeta = {
  googleSheets: {
    key: "googleSheets",
    title: "Google Sheets",
    eyebrow: "Quick connect",
    description:
      "Fastest way to connect shared account or inventory data without a heavier CRM rollout.",
    icon: FileSpreadsheet,
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    iconClass: "text-emerald-600",
    cta: "Connect Google Sheets",
    reconnectLabel: "Update Google Sheets",
    helpHref: "https://support.google.com/docs/answer/9331167?hl=en",
    helpLabel: "Open Sheets help",
    targetsLabel: "Import from this sheet",
    unlocks: [
      "Shared inventory source for warehouse or ops teams.",
      "Account import staging without CSV handoffs.",
      "An easier starting point before a larger CRM connection.",
    ],
  },
  quickbooks: {
    key: "quickbooks",
    title: "QuickBooks",
    eyebrow: "Finance sync",
    description:
      "Use QuickBooks as the source for customers, items, and finance-adjacent import workflows.",
    icon: Receipt,
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    iconClass: "text-amber-600",
    cta: "Save QuickBooks setup",
    reconnectLabel: "Update QuickBooks setup",
    helpHref: "https://developer.intuit.com/app/developer/qbo/docs/get-started",
    helpLabel: "Open QuickBooks docs",
    targetsLabel: "Use QuickBooks for",
    unlocks: [
      "Bring customer/account records in from your accounting system.",
      "Stage product or item imports from the same finance source.",
      "Prepare a cleaner future sync for orders and invoicing workflows.",
    ],
  },
  hubspot: {
    key: "hubspot",
    title: "HubSpot",
    eyebrow: "CRM lite",
    description:
      "A lighter-weight CRM connection for companies, contacts, and commercial context without full Salesforce complexity.",
    icon: Globe2,
    badgeClass: "bg-orange-50 text-orange-700 border-orange-200",
    iconClass: "text-orange-600",
    cta: "Save HubSpot setup",
    reconnectLabel: "Update HubSpot setup",
    helpHref: "https://developers.hubspot.com/docs/api/working-with-oauth",
    helpLabel: "Open HubSpot docs",
    targetsLabel: "Use HubSpot for",
    unlocks: [
      "Import companies as SupplyHawk accounts.",
      "Bring HubSpot contacts in under those accounts.",
      "Prepare a cleaner CRM handoff without a heavy enterprise setup.",
    ],
  },
  salesforce: {
    key: "salesforce",
    title: "Salesforce",
    eyebrow: "CRM sync",
    description:
      "Best for account ownership, rep assignment, and richer sales workflows once you are ready for CRM sync.",
    icon: Cable,
    badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
    iconClass: "text-blue-600",
    cta: "Connect Salesforce",
    reconnectLabel: "Reconnect Salesforce",
    helpHref: "https://www.salesforce.com/",
    helpLabel: "Open Salesforce",
    targetsLabel: "Use Salesforce for",
    unlocks: [
      "Account sync into SupplyHawk account records.",
      "Assigned rep mapping from Salesforce owners.",
      "Sample and order activity push-back to your CRM process.",
    ],
  },
};

const targetLabels = {
  accounts: "Accounts",
  inventory: "Inventory",
  contacts: "Contacts",
};

const normalizeTargets = (targets) =>
  Array.from(new Set((Array.isArray(targets) ? targets : []).filter(Boolean)));

const buildGoogleSheetsState = (connection) => ({
  workspaceName: connection?.workspaceName || "",
  spreadsheetUrl: connection?.spreadsheetUrl || "",
  spreadsheetId: connection?.spreadsheetId || "",
  sheetName: connection?.sheetName || "",
  spreadsheetRange: connection?.spreadsheetRange || "Sheet1!A1:Z1000",
  accessToken: "",
  syncDirection: connection?.syncDirection || "import-only",
  importTargets: normalizeTargets(connection?.importTargets || ["inventory"]),
});

const buildQuickBooksState = (connection) => ({
  companyName: connection?.companyName || "",
  environment: connection?.environment || "production",
  realmId: connection?.realmId || "",
  clientId: "",
  clientSecret: "",
  redirectUri: connection?.redirectUri || "",
  accessToken: "",
  refreshToken: "",
  importTargets: normalizeTargets(connection?.importTargets || ["accounts"]),
});

const buildSalesforceState = (connection) => ({
  connectedAppName: connection?.connectedAppName || "",
  loginUrl: connection?.loginUrl || "https://login.salesforce.com",
  clientId: "",
  clientSecret: "",
  username: connection?.username || "",
  password: "",
  securityToken: "",
  importTargets: normalizeTargets(connection?.importTargets || ["accounts", "contacts"]),
  importSalesforceAccounts: connection?.importSalesforceAccounts === true,
  mapSalesforceOwnersToAssignedReps: connection?.mapSalesforceOwnersToAssignedReps === true,
});

const buildHubSpotState = (connection) => ({
  portalId: connection?.portalId || "",
  appLabel: connection?.appLabel || "",
  privateAccessToken: "",
  importTargets: normalizeTargets(connection?.importTargets || ["accounts", "contacts"]),
});

const statusLabel = (connection) => {
  const raw = String(connection?.status || "").trim();
  if (!raw) return "Not connected";
  if (raw === "configured") return "Configured";
  if (raw === "connected") return "Connected";
  return raw.replace(/_/g, " ");
};

const buildImportSummaryCards = (target, result) => {
  const summary = result?.summary || {};
  const isInventory = target === "inventory";

  if (!isInventory) {
    return [
      {
        label: "Fetched",
        value: summary.totalFetched ?? 0,
        tone: "slate",
      },
      {
        label: result?.mode === "preview" ? "Would create" : "Created",
        value: summary.wouldCreate ?? summary.created ?? 0,
        tone: "emerald",
      },
      {
        label: result?.mode === "preview" ? "Would update" : "Updated",
        value: summary.wouldUpdate ?? summary.updated ?? 0,
        tone: "blue",
      },
      {
        label: "Skipped",
        value: summary.skipped ?? 0,
        tone: "amber",
      },
    ];
  }

  return [
    {
      label: "Fetched",
      value: summary.totalFetched ?? 0,
      tone: "slate",
      helper: "Rows pulled from the source connection.",
    },
    {
      label: result?.mode === "preview" ? "Would create" : "Created",
      value: summary.wouldCreate ?? summary.created ?? 0,
      tone: "emerald",
      helper: "New inventory items with no external ID or SKU match.",
    },
    {
      label: "Matched by external ID",
      value: summary.matchedByExternalId ?? 0,
      tone: "blue",
      helper: "Safest overwrite path when the source sends a stable product ID.",
    },
    {
      label: "Matched by SKU",
      value: summary.matchedBySku ?? 0,
      tone: "indigo",
      helper: "Overwrite path when there is no external ID but the current SKU or a previous SKU already exists.",
    },
    {
      label: "Skipped",
      value: summary.skipped ?? 0,
      tone: "amber",
      helper: "Rows skipped because they need manual review.",
    },
    {
      label: "Missing identifier",
      value: summary.missingIdentifier ?? 0,
      tone: "amber",
      helper: "Rows with no external ID and no SKU, so SupplyHawk will not guess.",
    },
    {
      label: "Identifier conflicts",
      value: summary.identifierConflict ?? 0,
      tone: "rose",
      helper: "External ID and SKU point at different existing items.",
    },
  ];
};

const summaryCardTone = (tone) => {
  switch (tone) {
    case "emerald":
      return "border-emerald-200 bg-emerald-50 text-emerald-900";
    case "blue":
      return "border-blue-200 bg-blue-50 text-blue-900";
    case "indigo":
      return "border-indigo-200 bg-indigo-50 text-indigo-900";
    case "amber":
      return "border-amber-200 bg-amber-50 text-amber-900";
    case "rose":
      return "border-rose-200 bg-rose-50 text-rose-900";
    default:
      return "border-slate-200 bg-slate-50 text-slate-900";
  }
};

const inventoryDecisionLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  if (raw === "matchedExternalId") return "Will update by external ID";
  if (raw === "matchedSku") return "Will update by SKU";
  if (raw === "newItem") return "Will create new item";
  if (raw === "missingIdentifier") return "Needs identifier";
  if (raw === "identifierConflict") return "Identifier conflict";
  if (raw === "missingName") return "Missing name";
  return raw.replace(/([A-Z])/g, " $1").replace(/_/g, " ");
};

const summarizeReviewDecisions = (decisionMap = {}) =>
  Object.values(decisionMap || {}).reduce(
    (summary, decision) => {
      const action = String(decision?.action || "").trim();
      if (action) summary.actions[action] = (summary.actions[action] || 0) + 1;
      if (decision?.externalId) summary.suppliedExternalIds += 1;
      if (decision?.sku) summary.suppliedSkus += 1;
      summary.total += 1;
      return summary;
    },
    { total: 0, suppliedExternalIds: 0, suppliedSkus: 0, actions: {} }
  );

function TargetCheckboxes({ options, selectedValues, onToggle, label }) {
  return (
    <div className="md:col-span-2">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="grid gap-2 sm:grid-cols-3">
        {options.map((option) => {
          const checked = selectedValues.includes(option.value);
          return (
            <label
              key={option.value}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                checked
                  ? "border-blue-300 bg-blue-50 text-blue-900"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              }`}
            >
              <input
                type="checkbox"
                className={checkboxClasses}
                checked={checked}
                onChange={() => onToggle(option.value)}
              />
              <span>{option.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminConnectionsTab({
  companyId,
  company,
  isAdminOrOwner,
  isOwner,
  onRefreshCompany,
}) {
  const integrations = useMemo(() => company?.integrations || {}, [company?.integrations]);
  const [activeConnector, setActiveConnector] = useState("googleSheets");
  const [googleSheetsForm, setGoogleSheetsForm] = useState(buildGoogleSheetsState(integrations.googleSheets));
  const [quickBooksForm, setQuickBooksForm] = useState(buildQuickBooksState(integrations.quickbooks));
  const [hubSpotForm, setHubSpotForm] = useState(buildHubSpotState(integrations.hubspot));
  const [salesforceForm, setSalesforceForm] = useState(buildSalesforceState(integrations.salesforce));
  const [savingKey, setSavingKey] = useState("");
  const [disconnectingKey, setDisconnectingKey] = useState("");
  const [previewingTarget, setPreviewingTarget] = useState("");
  const [importingTarget, setImportingTarget] = useState("");
  const [importResults, setImportResults] = useState({});
  const [importReviewDecisions, setImportReviewDecisions] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setGoogleSheetsForm(buildGoogleSheetsState(integrations.googleSheets));
  }, [integrations.googleSheets]);

  useEffect(() => {
    setQuickBooksForm(buildQuickBooksState(integrations.quickbooks));
  }, [integrations.quickbooks]);

  useEffect(() => {
    setSalesforceForm(buildSalesforceState(integrations.salesforce));
  }, [integrations.salesforce]);

  useEffect(() => {
    setHubSpotForm(buildHubSpotState(integrations.hubspot));
  }, [integrations.hubspot]);

  useEffect(() => {
    const firstConnected = connectorOrder.find((key) => integrations?.[key]?.status && integrations[key].status !== "disconnected");
    if (firstConnected) {
      setActiveConnector((prev) => prev || firstConnected);
    }
  }, [integrations]);

  const connectGoogleSheets = useMemo(() => httpsCallable(functions, "connectGoogleSheets"), []);
  const disconnectGoogleSheets = useMemo(() => httpsCallable(functions, "disconnectGoogleSheets"), []);
  const connectQuickBooks = useMemo(() => httpsCallable(functions, "connectQuickBooks"), []);
  const disconnectQuickBooks = useMemo(() => httpsCallable(functions, "disconnectQuickBooks"), []);
  const connectHubSpot = useMemo(() => httpsCallable(functions, "connectHubSpot"), []);
  const disconnectHubSpot = useMemo(() => httpsCallable(functions, "disconnectHubSpot"), []);
  const connectSalesforce = useMemo(() => httpsCallable(functions, "connectSalesforce"), []);
  const disconnectSalesforce = useMemo(() => httpsCallable(functions, "disconnectSalesforce"), []);
  const previewConnectionImport = useMemo(() => httpsCallable(functions, "previewConnectionImport"), []);
  const runConnectionImport = useMemo(() => httpsCallable(functions, "runConnectionImport"), []);

  if (!isAdminOrOwner) return null;

  const activeConnection = integrations?.[activeConnector] || null;
  const activeMeta = connectorMeta[activeConnector];
  const ActiveIcon = activeMeta.icon;
  const activeSteps = stepContent[activeConnector] || [];
  const isConnected = activeConnection?.status && activeConnection.status !== "disconnected";

  const toggleTarget = (formSetter, formState, value) => {
    const next = formState.importTargets.includes(value)
      ? formState.importTargets.filter((entry) => entry !== value)
      : [...formState.importTargets, value];
    formSetter((prev) => ({ ...prev, importTargets: next }));
  };

  const runSave = async (key, callback) => {
    setSavingKey(key);
    setError("");
    setSuccess("");
    try {
      await callback();
      await onRefreshCompany?.(companyId);
      setSuccess(`${connectorMeta[key].title} setup saved.`);
    } catch (err) {
      console.error(`[AdminConnectionsTab] ${key} connect failed:`, err);
      setError(err?.message || `Unable to save ${connectorMeta[key].title} right now.`);
    } finally {
      setSavingKey("");
    }
  };

  const runDisconnect = async (key, callback) => {
    const confirmed = window.confirm(`Disconnect ${connectorMeta[key].title} for this company?`);
    if (!confirmed) return;
    setDisconnectingKey(key);
    setError("");
    setSuccess("");
    try {
      await callback();
      await onRefreshCompany?.(companyId);
      setSuccess(`${connectorMeta[key].title} has been disconnected.`);
    } catch (err) {
      console.error(`[AdminConnectionsTab] ${key} disconnect failed:`, err);
      setError(err?.message || `Unable to disconnect ${connectorMeta[key].title} right now.`);
    } finally {
      setDisconnectingKey("");
    }
  };

  const handleSaveGoogleSheets = async () => {
    await runSave("googleSheets", async () => {
      await connectGoogleSheets({
        companyId,
        ...googleSheetsForm,
      });
    });
  };

  const handleSaveQuickBooks = async () => {
    await runSave("quickbooks", async () => {
      await connectQuickBooks({
        companyId,
        ...quickBooksForm,
      });
      setQuickBooksForm((prev) => ({
        ...prev,
        clientSecret: "",
        accessToken: "",
        refreshToken: "",
        clientId: prev.clientId,
      }));
    });
  };

  const handleSaveSalesforce = async () => {
    await runSave("salesforce", async () => {
      await connectSalesforce({
        companyId,
        ...salesforceForm,
      });
      setSalesforceForm((prev) => ({
        ...prev,
        clientSecret: "",
        password: "",
        securityToken: "",
      }));
    });
  };

  const handleSaveHubSpot = async () => {
    await runSave("hubspot", async () => {
      await connectHubSpot({
        companyId,
        ...hubSpotForm,
      });
      setHubSpotForm((prev) => ({
        ...prev,
        privateAccessToken: "",
      }));
    });
  };

  const renderForm = () => {
    if (activeConnector === "googleSheets") {
      return (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Workspace label</div>
            <input
              value={googleSheetsForm.workspaceName}
              onChange={(event) =>
                setGoogleSheetsForm((prev) => ({ ...prev, workspaceName: event.target.value }))
              }
              className={inputClasses}
              placeholder="Warehouse import sheet"
            />
          </label>
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Default tab name</div>
            <input
              value={googleSheetsForm.sheetName}
              onChange={(event) =>
                setGoogleSheetsForm((prev) => ({ ...prev, sheetName: event.target.value }))
              }
              className={inputClasses}
              placeholder="Inventory"
            />
          </label>
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Range</div>
            <input
              value={googleSheetsForm.spreadsheetRange}
              onChange={(event) =>
                setGoogleSheetsForm((prev) => ({ ...prev, spreadsheetRange: event.target.value }))
              }
              className={inputClasses}
              placeholder="Sheet1!A1:Z1000"
            />
          </label>
          <label className="block md:col-span-2">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Google Sheet URL or spreadsheet id</div>
            <input
              value={googleSheetsForm.spreadsheetUrl}
              onChange={(event) =>
                setGoogleSheetsForm((prev) => ({
                  ...prev,
                  spreadsheetUrl: event.target.value,
                  spreadsheetId: event.target.value,
                }))
              }
              className={inputClasses}
              placeholder="https://docs.google.com/spreadsheets/d/..."
            />
          </label>
          <label className="block md:col-span-2">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Access token</div>
            <input
              type="password"
              value={googleSheetsForm.accessToken}
              onChange={(event) =>
                setGoogleSheetsForm((prev) => ({ ...prev, accessToken: event.target.value }))
              }
              className={inputClasses}
              placeholder="Google OAuth access token"
            />
          </label>
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Sync direction</div>
            <select
              value={googleSheetsForm.syncDirection}
              onChange={(event) =>
                setGoogleSheetsForm((prev) => ({ ...prev, syncDirection: event.target.value }))
              }
              className={inputClasses}
            >
              <option value="import-only">Import only</option>
              <option value="two-way-review">Two-way review</option>
              <option value="warehouse-dropzone">Warehouse drop zone</option>
            </select>
          </label>
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Paste the Google Sheet URL your team already uses. This is the fastest option for warehouses, ops teams,
            or shared inventory/account sheets.
          </div>
          <TargetCheckboxes
            label={activeMeta.targetsLabel}
            options={[
              { value: "inventory", label: "Inventory" },
              { value: "accounts", label: "Accounts" },
              { value: "contacts", label: "Contacts" },
            ]}
            selectedValues={googleSheetsForm.importTargets}
            onToggle={(value) => toggleTarget(setGoogleSheetsForm, googleSheetsForm, value)}
          />
        </div>
      );
    }

    if (activeConnector === "quickbooks") {
      return (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Company name</div>
            <input
              value={quickBooksForm.companyName}
              onChange={(event) =>
                setQuickBooksForm((prev) => ({ ...prev, companyName: event.target.value }))
              }
              className={inputClasses}
              placeholder="SupplyHawk Distribution"
            />
          </label>
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Environment</div>
            <select
              value={quickBooksForm.environment}
              onChange={(event) =>
                setQuickBooksForm((prev) => ({ ...prev, environment: event.target.value }))
              }
              className={inputClasses}
            >
              <option value="production">Production</option>
              <option value="sandbox">Sandbox</option>
            </select>
          </label>
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Realm ID</div>
            <input
              value={quickBooksForm.realmId}
              onChange={(event) =>
                setQuickBooksForm((prev) => ({ ...prev, realmId: event.target.value }))
              }
              className={inputClasses}
              placeholder="123145960000000"
            />
          </label>
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Redirect URI</div>
            <input
              value={quickBooksForm.redirectUri}
              onChange={(event) =>
                setQuickBooksForm((prev) => ({ ...prev, redirectUri: event.target.value }))
              }
              className={inputClasses}
              placeholder="https://your-app.example.com/callback"
            />
          </label>
          <label className="block md:col-span-2">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Client ID</div>
            <input
              value={quickBooksForm.clientId}
              onChange={(event) =>
                setQuickBooksForm((prev) => ({ ...prev, clientId: event.target.value }))
              }
              className={inputClasses}
              placeholder="QuickBooks client id"
            />
          </label>
          <label className="block md:col-span-2">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Client Secret</div>
            <input
              type="password"
              value={quickBooksForm.clientSecret}
              onChange={(event) =>
                setQuickBooksForm((prev) => ({ ...prev, clientSecret: event.target.value }))
              }
              className={inputClasses}
              placeholder="QuickBooks client secret"
            />
          </label>
          <label className="block md:col-span-2">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Access token</div>
            <input
              type="password"
              value={quickBooksForm.accessToken}
              onChange={(event) =>
                setQuickBooksForm((prev) => ({ ...prev, accessToken: event.target.value }))
              }
              className={inputClasses}
              placeholder="QuickBooks OAuth access token"
            />
          </label>
          <label className="block md:col-span-2">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Refresh token</div>
            <input
              type="password"
              value={quickBooksForm.refreshToken}
              onChange={(event) =>
                setQuickBooksForm((prev) => ({ ...prev, refreshToken: event.target.value }))
              }
              className={inputClasses}
              placeholder="Optional QuickBooks refresh token"
            />
          </label>
          <TargetCheckboxes
            label={activeMeta.targetsLabel}
            options={[
              { value: "accounts", label: "Customers / Accounts" },
              { value: "inventory", label: "Items / Inventory" },
              { value: "contacts", label: "Contacts" },
            ]}
            selectedValues={quickBooksForm.importTargets}
            onToggle={(value) => toggleTarget(setQuickBooksForm, quickBooksForm, value)}
          />
        </div>
      );
    }

    if (activeConnector === "hubspot") {
      return (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Portal ID</div>
            <input
              value={hubSpotForm.portalId}
              onChange={(event) =>
                setHubSpotForm((prev) => ({ ...prev, portalId: event.target.value }))
              }
              className={inputClasses}
              placeholder="Optional HubSpot portal id"
            />
          </label>
          <label className="block">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Private app label</div>
            <input
              value={hubSpotForm.appLabel}
              onChange={(event) =>
                setHubSpotForm((prev) => ({ ...prev, appLabel: event.target.value }))
              }
              className={inputClasses}
              placeholder="SupplyHawk HubSpot app"
            />
          </label>
          <label className="block md:col-span-2">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Private app access token</div>
            <input
              type="password"
              value={hubSpotForm.privateAccessToken}
              onChange={(event) =>
                setHubSpotForm((prev) => ({ ...prev, privateAccessToken: event.target.value }))
              }
              className={inputClasses}
              placeholder="pat-..."
            />
          </label>
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600 md:col-span-2">
            Use a HubSpot private app token if this is for one account, or treat this as the first setup pass before
            a fuller OAuth install flow later.
          </div>
          <TargetCheckboxes
            label={activeMeta.targetsLabel}
            options={[
              { value: "accounts", label: "Companies / Accounts" },
              { value: "contacts", label: "Contacts" },
              { value: "inventory", label: "Products" },
            ]}
            selectedValues={hubSpotForm.importTargets}
            onToggle={(value) => toggleTarget(setHubSpotForm, hubSpotForm, value)}
          />
        </div>
      );
    }

    return (
      <div className="mt-5 grid gap-3 md:grid-cols-2">
        <label className="block">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Connected App Name</div>
          <input
            value={salesforceForm.connectedAppName}
            onChange={(event) =>
              setSalesforceForm((prev) => ({ ...prev, connectedAppName: event.target.value }))
            }
            className={inputClasses}
            placeholder="SupplyHawk Connected App"
          />
        </label>
        <label className="block">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Login URL</div>
          <input
            value={salesforceForm.loginUrl}
            onChange={(event) =>
              setSalesforceForm((prev) => ({ ...prev, loginUrl: event.target.value }))
            }
            className={inputClasses}
            placeholder="https://login.salesforce.com"
          />
        </label>
        <label className="block md:col-span-2">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Consumer Key / Client ID</div>
          <input
            value={salesforceForm.clientId}
            onChange={(event) =>
              setSalesforceForm((prev) => ({ ...prev, clientId: event.target.value }))
            }
            className={inputClasses}
            placeholder="Enter your Salesforce Consumer Key"
          />
        </label>
        <label className="block md:col-span-2">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Consumer Secret / Client Secret</div>
          <input
            type="password"
            value={salesforceForm.clientSecret}
            onChange={(event) =>
              setSalesforceForm((prev) => ({ ...prev, clientSecret: event.target.value }))
            }
            className={inputClasses}
            placeholder="Enter your Salesforce Consumer Secret"
          />
        </label>
        <label className="block">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Username</div>
          <input
            value={salesforceForm.username}
            onChange={(event) =>
              setSalesforceForm((prev) => ({ ...prev, username: event.target.value }))
            }
            className={inputClasses}
            placeholder="api.user@company.com"
          />
        </label>
        <label className="block">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Password</div>
          <input
            type="password"
            value={salesforceForm.password}
            onChange={(event) =>
              setSalesforceForm((prev) => ({ ...prev, password: event.target.value }))
            }
            className={inputClasses}
            placeholder="Salesforce password"
          />
        </label>
        <label className="block md:col-span-2">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Security Token</div>
          <input
            type="password"
            value={salesforceForm.securityToken}
            onChange={(event) =>
              setSalesforceForm((prev) => ({ ...prev, securityToken: event.target.value }))
            }
            className={inputClasses}
            placeholder="Optional, if your org requires a security token"
          />
        </label>
        <TargetCheckboxes
          label={activeMeta.targetsLabel}
          options={[
            { value: "accounts", label: "Accounts" },
            { value: "contacts", label: "Contacts" },
            { value: "inventory", label: "Inventory" },
          ]}
          selectedValues={salesforceForm.importTargets}
          onToggle={(value) => toggleTarget(setSalesforceForm, salesforceForm, value)}
        />
        <div className="md:col-span-2">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">When Salesforce is connected</div>
          <div className="space-y-2">
            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                className={`${checkboxClasses} mt-0.5`}
                checked={salesforceForm.importSalesforceAccounts}
                onChange={(event) =>
                  setSalesforceForm((prev) => ({
                    ...prev,
                    importSalesforceAccounts: event.target.checked,
                    importTargets: event.target.checked
                      ? normalizeTargets([...prev.importTargets, "accounts"])
                      : prev.importTargets,
                  }))
                }
              />
              <span>
                <span className="font-medium text-slate-900">Import Salesforce Accounts into SupplyHawk Accounts</span>
                <span className="mt-0.5 block text-slate-600">
                  Use Salesforce account data as the source for SupplyHawk account imports.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                className={`${checkboxClasses} mt-0.5`}
                checked={salesforceForm.mapSalesforceOwnersToAssignedReps}
                onChange={(event) =>
                  setSalesforceForm((prev) => ({
                    ...prev,
                    mapSalesforceOwnersToAssignedReps: event.target.checked,
                  }))
                }
              />
              <span>
                <span className="font-medium text-slate-900">Map Salesforce account owners to assigned reps</span>
                <span className="mt-0.5 block text-slate-600">
                  If the owner exists in SupplyHawk, use that Salesforce owner as the assigned rep on imported accounts.
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>
    );
  };

  const handleSave = async () => {
    if (!companyId) return;
    if (activeConnector === "googleSheets") return handleSaveGoogleSheets();
    if (activeConnector === "quickbooks") return handleSaveQuickBooks();
    if (activeConnector === "hubspot") return handleSaveHubSpot();
    return handleSaveSalesforce();
  };

  const handleDisconnect = async () => {
    if (!companyId) return;
    if (activeConnector === "googleSheets") {
      return runDisconnect("googleSheets", async () => {
        await disconnectGoogleSheets({ companyId });
      });
    }
    if (activeConnector === "quickbooks") {
      return runDisconnect("quickbooks", async () => {
        await disconnectQuickBooks({ companyId });
      });
    }
    if (activeConnector === "hubspot") {
      return runDisconnect("hubspot", async () => {
        await disconnectHubSpot({ companyId });
      });
    }
    return runDisconnect("salesforce", async () => {
      await disconnectSalesforce({ companyId });
    });
  };

  const activeImportTargets = normalizeTargets(activeConnection?.importTargets || []);

  const handlePreviewImport = async (target) => {
    if (!companyId) return;
    setPreviewingTarget(target);
    setError("");
    try {
      const resultKey = `${activeConnector}:${target}`;
      const reviewDecisionMap = importReviewDecisions[resultKey] || {};
      const response = await previewConnectionImport({
        companyId,
        provider: activeConnector,
        target,
        reviewDecisions: Object.values(reviewDecisionMap),
      });
      setImportResults((prev) => ({
        ...prev,
        [resultKey]: {
          ...(response.data || {}),
          mode: "preview",
        },
      }));
      try {
        await logAuditEvent(getAuth().currentUser, {
          action: "connection_import_previewed",
          entityType: "connectionImport",
          entityId: resultKey,
          metadata: {
            provider: activeConnector,
            target,
            reviewDecisions: summarizeReviewDecisions(reviewDecisionMap),
            summary: response.data?.summary || null,
          },
        });
      } catch (auditErr) {
        console.warn("[AdminConnectionsTab] Failed to audit import preview:", auditErr);
      }
    } catch (err) {
      console.error("[AdminConnectionsTab] preview import failed:", err);
      setError(err?.message || "Unable to preview this import right now.");
    } finally {
      setPreviewingTarget("");
    }
  };

  const handleRunImport = async (target) => {
    if (!companyId) return;
    setImportingTarget(target);
    setError("");
    try {
      const resultKey = `${activeConnector}:${target}`;
      const reviewDecisionMap = importReviewDecisions[resultKey] || {};
      const response = await runConnectionImport({
        companyId,
        provider: activeConnector,
        target,
        reviewDecisions: Object.values(reviewDecisionMap),
      });
      setImportResults((prev) => ({
        ...prev,
        [resultKey]: {
          ...(response.data || {}),
          mode: "import",
        },
      }));
      setSuccess(`${targetLabels[target] || target} import finished.`);
      try {
        await logAuditEvent(getAuth().currentUser, {
          action: "connection_import_run",
          entityType: "connectionImport",
          entityId: resultKey,
          metadata: {
            provider: activeConnector,
            target,
            reviewDecisions: summarizeReviewDecisions(reviewDecisionMap),
            summary: response.data?.summary || null,
            mode: "import",
          },
          severity: "warning",
        });
      } catch (auditErr) {
        console.warn("[AdminConnectionsTab] Failed to audit import run:", auditErr);
      }
    } catch (err) {
      console.error("[AdminConnectionsTab] run import failed:", err);
      setError(err?.message || "Unable to run this import right now.");
    } finally {
      setImportingTarget("");
    }
  };

  const updateReviewDecision = (resultKey, rowIndex, patch) => {
    setImportReviewDecisions((prev) => ({
      ...prev,
      [resultKey]: {
        ...(prev[resultKey] || {}),
        [rowIndex]: {
          rowIndex,
          ...((prev[resultKey] || {})[rowIndex] || {}),
          ...patch,
        },
      },
    }));
  };

  const isReviewRowResolved = (row, decision) => {
    const reason = String(row?.decision || "").trim();
    if (reason === "identifierConflict") {
      return ["useExternalMatch", "useSkuMatch", "skip"].includes(String(decision?.action || "").trim());
    }
    if (reason === "missingIdentifier") {
      return (
        String(decision?.action || "").trim() === "skip" ||
        !!String(decision?.externalId || "").trim() ||
        !!String(decision?.sku || "").trim()
      );
    }
    if (reason === "missingName") {
      return String(decision?.action || "").trim() === "skip";
    }
    return true;
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2">
              <PackageSearch className="h-5 w-5 text-blue-600" />
              <h2 className="text-base font-semibold text-slate-900">Import Connections</h2>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Pick the system your team already trusts, then walk through the setup for that connection. This is the new
              home for import-ready connections before you bring in accounts, inventory, or contact data.
            </p>
          </div>
          <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <div className="font-semibold">Suggested order</div>
            <div className="mt-1 text-blue-800">
              Start with Google Sheets or QuickBooks first, then layer Salesforce in when you want rep and CRM sync.
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.9fr_1.2fr_0.85fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-900">Choose a connection</div>
          <div className="mt-4 space-y-3">
            {connectorOrder.map((key) => {
              const meta = connectorMeta[key];
              const Icon = meta.icon;
              const isActive = activeConnector === key;
              const connection = integrations?.[key] || null;
              const label = statusLabel(connection);

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveConnector(key)}
                  className={`w-full rounded-2xl border p-4 text-left transition ${
                    isActive
                      ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isActive ? "text-slate-300" : "text-slate-500"}`}>
                        {meta.eyebrow}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${isActive ? "text-white" : meta.iconClass}`} />
                        <div className="text-sm font-semibold">{meta.title}</div>
                      </div>
                      <div className={`mt-2 text-sm leading-5 ${isActive ? "text-slate-200" : "text-slate-600"}`}>
                        {meta.description}
                      </div>
                    </div>
                    <ChevronRight className={`mt-0.5 h-4 w-4 ${isActive ? "text-slate-300" : "text-slate-400"}`} />
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                        isActive ? "border-slate-700 bg-slate-800 text-slate-100" : meta.badgeClass
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <ActiveIcon className={`h-5 w-5 ${activeMeta.iconClass}`} />
                <h3 className="text-base font-semibold text-slate-900">{activeMeta.title} setup</h3>
              </div>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">{activeMeta.description}</p>
            </div>
            <a
              href={activeMeta.helpHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {activeMeta.helpLabel}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>

          <div className="mt-4 space-y-3">
            {activeSteps.map((step, index) => (
              <div key={step} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                <div className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                  {index + 1}
                </div>
                <div className="text-sm text-slate-700">{step}</div>
              </div>
            ))}
          </div>

          {renderForm()}

          {error ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}
          {success ? (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {success}
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              Connection settings are stored for this company and can be reused as your import source of truth.
            </div>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={disconnectingKey === activeConnector}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
                >
                  {disconnectingKey === activeConnector ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Unplug className="h-4 w-4" />
                  )}
                  Disconnect
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleSave}
                disabled={savingKey === activeConnector}
                className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {savingKey === activeConnector ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Link2 className="h-4 w-4" />
                )}
                {isConnected ? activeMeta.reconnectLabel : activeMeta.cta}
              </button>
            </div>
          </div>

          {activeImportTargets.length ? (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900">Import actions</div>
              <div className="mt-3 space-y-3">
                {activeImportTargets.map((target) => {
                  const resultKey = `${activeConnector}:${target}`;
                  const result = importResults[resultKey];
                  const reviewDecisionMap = importReviewDecisions[resultKey] || {};
                  const unresolvedReviewCount =
                    target === "inventory"
                      ? (result?.reviewRows || []).filter(
                          (row) => !isReviewRowResolved(row, reviewDecisionMap[row.rowIndex])
                        ).length
                      : 0;
                  return (
                    <div key={target} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{targetLabels[target] || target}</div>
                          <div className="text-xs text-slate-500">
                            Preview what will be imported, then run the live import into SupplyHawk.
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handlePreviewImport(target)}
                            disabled={previewingTarget === target || importingTarget === target}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {previewingTarget === target ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                            Preview import
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRunImport(target)}
                            disabled={
                              importingTarget === target ||
                              previewingTarget === target ||
                              unresolvedReviewCount > 0
                            }
                            className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                          >
                            {importingTarget === target ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                            Run import
                          </button>
                        </div>
                      </div>

                      {unresolvedReviewCount > 0 ? (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                          Resolve {unresolvedReviewCount} inventory review
                          {unresolvedReviewCount === 1 ? " item" : " items"} below before running the import.
                        </div>
                      ) : null}

                      {result?.summary ? (
                        <div className="mt-3 space-y-3">
                          {target === "inventory" ? (
                            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                              Inventory imports overwrite existing items by real source ID first, then by current or previous SKU. New
                              items are only created when neither identifier matches.
                            </div>
                          ) : null}

                          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                            {buildImportSummaryCards(target, result).map((card) => (
                              <div
                                key={card.label}
                                className={`rounded-lg border px-3 py-2 text-xs ${summaryCardTone(card.tone)}`}
                              >
                                <div className="font-semibold">{card.label}</div>
                                <div className="mt-1 text-base font-semibold">{card.value}</div>
                                {card.helper ? (
                                  <div className="mt-1 leading-4 opacity-80">{card.helper}</div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {target === "inventory" && Array.isArray(result?.reviewRows) && result.reviewRows.length ? (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                            Needs review
                          </div>
                          <div className="mt-2 space-y-2">
                            {result.reviewRows.slice(0, 6).map((row, index) => {
                              const reviewDecision = reviewDecisionMap[row.rowIndex] || {};
                              const resolved = isReviewRowResolved(row, reviewDecision);
                              return (
                                <div
                                  key={`review-${target}-${index}`}
                                  className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700"
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="font-semibold text-slate-900">
                                      {row.name || row.sku || row.externalId || "Unnamed row"}
                                    </div>
                                    <div
                                      className={`rounded-full px-2 py-0.5 font-medium ${
                                        resolved
                                          ? "bg-emerald-100 text-emerald-800"
                                          : "bg-amber-100 text-amber-800"
                                      }`}
                                    >
                                      {resolved ? "Ready" : inventoryDecisionLabel(row.decision)}
                                    </div>
                                  </div>
                                  <div className="mt-1 text-[11px] text-slate-500">
                                    SKU: {row.sku || "—"} • External ID: {row.externalId || "—"}
                                  </div>
                                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                                    <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2">
                                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                        Incoming row
                                      </div>
                                      <div className="mt-1 font-medium text-slate-900">{row.name || "—"}</div>
                                      <div className="text-[11px] text-slate-500">
                                        {row.category || "No category"} • {row.sku || "No SKU"}
                                      </div>
                                    </div>
                                    <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2">
                                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                        Existing match
                                      </div>
                                      <div className="mt-1 font-medium text-slate-900">
                                        {row.currentMatch?.name ||
                                          row.externalIdMatch?.name ||
                                          row.skuMatch?.name ||
                                          "No single match"}
                                      </div>
                                      <div className="text-[11px] text-slate-500">
                                        {row.currentMatch?.sku ||
                                          row.externalIdMatch?.sku ||
                                          row.skuMatch?.sku ||
                                          "No SKU"}
                                      </div>
                                    </div>
                                  </div>

                                  {row.decision === "identifierConflict" ? (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateReviewDecision(resultKey, row.rowIndex, {
                                            action: "useExternalMatch",
                                          })
                                        }
                                        className={`rounded-lg border px-3 py-1.5 text-[11px] font-medium ${
                                          reviewDecision.action === "useExternalMatch"
                                            ? "border-blue-300 bg-blue-50 text-blue-900"
                                            : "border-slate-200 bg-white text-slate-700"
                                        }`}
                                      >
                                        Use external ID match
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateReviewDecision(resultKey, row.rowIndex, {
                                            action: "useSkuMatch",
                                          })
                                        }
                                        className={`rounded-lg border px-3 py-1.5 text-[11px] font-medium ${
                                          reviewDecision.action === "useSkuMatch"
                                            ? "border-blue-300 bg-blue-50 text-blue-900"
                                            : "border-slate-200 bg-white text-slate-700"
                                        }`}
                                      >
                                        Use SKU match
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateReviewDecision(resultKey, row.rowIndex, {
                                            action: "skip",
                                          })
                                        }
                                        className={`rounded-lg border px-3 py-1.5 text-[11px] font-medium ${
                                          reviewDecision.action === "skip"
                                            ? "border-amber-300 bg-amber-50 text-amber-900"
                                            : "border-slate-200 bg-white text-slate-700"
                                        }`}
                                      >
                                        Skip row
                                      </button>
                                    </div>
                                  ) : null}

                                  {row.decision === "missingIdentifier" ? (
                                    <div className="mt-2 grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                                      <input
                                        type="text"
                                        value={reviewDecision.externalId || ""}
                                        onChange={(event) =>
                                          updateReviewDecision(resultKey, row.rowIndex, {
                                            externalId: event.target.value,
                                          })
                                        }
                                        placeholder="Enter external ID"
                                        className={inputClasses}
                                      />
                                      <input
                                        type="text"
                                        value={reviewDecision.sku || ""}
                                        onChange={(event) =>
                                          updateReviewDecision(resultKey, row.rowIndex, {
                                            sku: event.target.value,
                                          })
                                        }
                                        placeholder="Enter SKU"
                                        className={inputClasses}
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateReviewDecision(resultKey, row.rowIndex, {
                                            action: "skip",
                                          })
                                        }
                                        className={`rounded-lg border px-3 py-1.5 text-[11px] font-medium ${
                                          reviewDecision.action === "skip"
                                            ? "border-amber-300 bg-amber-50 text-amber-900"
                                            : "border-slate-200 bg-white text-slate-700"
                                        }`}
                                      >
                                        Skip row
                                      </button>
                                    </div>
                                  ) : null}

                                  {row.decision === "missingName" ? (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateReviewDecision(resultKey, row.rowIndex, {
                                            action: "skip",
                                          })
                                        }
                                        className={`rounded-lg border px-3 py-1.5 text-[11px] font-medium ${
                                          reviewDecision.action === "skip"
                                            ? "border-amber-300 bg-amber-50 text-amber-900"
                                            : "border-slate-200 bg-white text-slate-700"
                                        }`}
                                      >
                                        Skip unnamed row
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

                      {Array.isArray(result?.sample) && result.sample.length ? (
                        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {target === "inventory" ? "Preview decisions" : "Preview sample"}
                          </div>
                          <div className="mt-2 space-y-2">
                            {target === "inventory"
                              ? result.sample.slice(0, 6).map((row, index) => (
                                  <div
                                    key={`${target}-${index}`}
                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700"
                                  >
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="font-semibold text-slate-900">
                                        {row.name || row.sku || row.externalId || "Unnamed row"}
                                      </div>
                                      <div className="text-[11px] font-medium text-slate-600">
                                        {inventoryDecisionLabel(row.decision)}
                                      </div>
                                    </div>
                                    <div className="mt-1 text-[11px] text-slate-500">
                                      SKU: {row.sku || "—"} • External ID: {row.externalId || "—"}
                                    </div>
                                  </div>
                                ))
                              : result.sample.slice(0, 3).map((row, index) => (
                                  <pre
                                    key={`${target}-${index}`}
                                    className="overflow-x-auto rounded-lg bg-white px-3 py-2 text-[11px] text-slate-700"
                                  >
                                    {JSON.stringify(row, null, 2)}
                                  </pre>
                                ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <h3 className="text-sm font-semibold text-slate-900">Connection Status</h3>
          </div>

          {!activeConnection || activeConnection?.status === "disconnected" ? (
            <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
              No {activeMeta.title} connection has been configured yet.
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <div className="text-sm font-semibold text-slate-900">{statusLabel(activeConnection)}</div>
                </div>
                <div className="mt-2 space-y-2 text-sm text-slate-600">
                  <div><span className="font-medium text-slate-800">Provider:</span> {activeMeta.title}</div>
                  {activeConnection?.workspaceName ? (
                    <div><span className="font-medium text-slate-800">Workspace:</span> {activeConnection.workspaceName}</div>
                  ) : null}
                  {activeConnection?.companyName ? (
                    <div><span className="font-medium text-slate-800">Company:</span> {activeConnection.companyName}</div>
                  ) : null}
                  {activeConnection?.orgName ? (
                    <div><span className="font-medium text-slate-800">Org:</span> {activeConnection.orgName}</div>
                  ) : null}
                  {activeConnection?.sheetName ? (
                    <div><span className="font-medium text-slate-800">Tab:</span> {activeConnection.sheetName}</div>
                  ) : null}
                  {activeConnection?.realmId ? (
                    <div><span className="font-medium text-slate-800">Realm ID:</span> {activeConnection.realmId}</div>
                  ) : null}
                  {activeConnection?.portalId ? (
                    <div><span className="font-medium text-slate-800">Portal ID:</span> {activeConnection.portalId}</div>
                  ) : null}
                  {activeConnection?.appLabel ? (
                    <div><span className="font-medium text-slate-800">App:</span> {activeConnection.appLabel}</div>
                  ) : null}
                  {activeConnection?.preferredUsername || activeConnection?.username ? (
                    <div><span className="font-medium text-slate-800">User:</span> {activeConnection.preferredUsername || activeConnection.username}</div>
                  ) : null}
                  <div><span className="font-medium text-slate-800">Connected by:</span> {activeConnection?.connectedByName || "—"}</div>
                  <div><span className="font-medium text-slate-800">Last updated:</span> {formatTimestamp(activeConnection?.connectedAt || activeConnection?.updatedAt)}</div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">What this unlocks next</div>
                <ul className="mt-2 space-y-2 text-sm text-slate-600">
                  {activeMeta.unlocks.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {!isOwner ? (
            <p className="mt-4 text-xs text-slate-500">
              Owners still control billing, but admins can configure import connections and keep setup moving.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
